import { Input } from "antd";
import MailingAddress from "components/widgets/WidgetBuilder/MailingAddress";
import {
	DEFAULT_FIELD_NAME,
	FIELD_NAME,
	SELECTION_TYPE,
	TYPE_CUSTOM_FIELD,
} from "constants/common";
import { WidgetFieldTypes } from "models/DynamicWidget";
import { ReactNode, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import utils from "services/utils";
import { setFieldRules } from "services/widgetBuilder";
import { StyledCol, StyledRow, StyledText } from "styled-components/Common/CommonStyled";
import {
	StyledFormItem,
	StyledSelect,
	StyledTextArea,
} from "styled-components/DynamicWidget/DynamicWidgetStyled";

type WidgetFieldProps = {
	field: WidgetFieldTypes;
	setFieldsValue: (values: any) => void;
	[others: string]: any;
};

const WidgetField = (props: WidgetFieldProps) => {
	const { t } = useTranslation();
	const { field, setFieldsValue, setFormValues, formValues } = props;
	const {
		key = "",
		fieldName = "",
		required = false,
		label = "",
		type = "",
		isContentDescription = false,
		listOption = [],
		name = "",
		answerType = "",
	} = field || {};
	const SPECIAL_FIELDS = [
		DEFAULT_FIELD_NAME.instagram,
		DEFAULT_FIELD_NAME.twitter,
		DEFAULT_FIELD_NAME.tiktok,
		DEFAULT_FIELD_NAME.youtube,
		DEFAULT_FIELD_NAME.phone,
	];
	const isSpecialFields = SPECIAL_FIELDS.includes(fieldName) || SPECIAL_FIELDS.includes(type);
	const span = isSpecialFields ? 12 : 24;

	const fieldDetails = useMemo(() => {
		let customLabel = label;
		let formItemFieldName = fieldName || key; // Initialize for CUSTOM fields

		const result = {};

		switch (true) {
			case type === TYPE_CUSTOM_FIELD.email:
				customLabel = t("form_label.email_address");
				formItemFieldName = FIELD_NAME.email;
				break;

			case type === TYPE_CUSTOM_FIELD.mailAddress:
				customLabel = t("form_label.mailing_address");
				formItemFieldName = DEFAULT_FIELD_NAME.mailingAddress;
				break;

			case type === TYPE_CUSTOM_FIELD.instagram:
				customLabel = t("form_label.instagram_profile");
				formItemFieldName = FIELD_NAME.instagram;
				break;

			case type === TYPE_CUSTOM_FIELD.tiktok:
				customLabel = t("form_label.tiktok_profile");
				formItemFieldName = FIELD_NAME.tiktok;
				break;

			case type === TYPE_CUSTOM_FIELD.twitter:
				customLabel = t("form_label.x_profile");
				formItemFieldName = FIELD_NAME.twitter;
				break;

			case type === TYPE_CUSTOM_FIELD.phone:
				customLabel = t("form_label.phone_number");
				formItemFieldName = FIELD_NAME.phone;
				break;

			case !!isContentDescription:
				customLabel = t("form_label.description");
				formItemFieldName = "description";
				break;

			default:
				break;
		}
		return { ...result, customLabel, formItemFieldName };
	}, [fieldName, type]);

	const renderLabel = (label?: string, className?: string) => {
		return (
			<StyledText className={className || ""} fontSize="12px">
				{`${label || fieldDetails.customLabel}${required ? " *" : ""}`}
			</StyledText>
		);
	};

	const renderOptions = (options: string[]) => {
		return options.map((option) => ({
			label: option,
			value: option,
		}));
	};

	const renderHelperText = () => {
		return <StyledText fontSize="10px">{name}</StyledText>;
	};

	const fieldProps: {
		margin?: string;
		label: string | ReactNode;
		rules: any[];
		name: string;
		help?: string | ReactNode;
	} = {
		margin: isSpecialFields ? "0 0 16px 0" : "0",
		label: renderLabel(),
		rules: setFieldRules(field, t),
		name: fieldDetails.formItemFieldName,
	};

	if ((isSpecialFields && name) || type === FIELD_NAME.email) {
		fieldProps.help = renderHelperText();
	}

	const wrapperRef = useRef<HTMLDivElement>(null);

	const handleFocus = () => {
		setTimeout(() => {
			wrapperRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}, 0); // Wait for mobile keyboard animation
	};

	const renderFormItemContent = () => {
		const selectProps: {
			allowClear: boolean;
			mode?: "multiple" | "tags" | undefined;
			maxTagCount?: number;
		} = { allowClear: true };
		if (answerType === SELECTION_TYPE.MULTIPLE) {
			selectProps.mode = "multiple";
			selectProps.allowClear = true;
			selectProps.maxTagCount = 5;
		}

		switch (true) {
			case type === TYPE_CUSTOM_FIELD.dropdown:
				return (
					<StyledSelect
						options={renderOptions(listOption || [])}
						placeholder={name}
						onFocus={(e) => {
							if (utils.isMobileWidth()) {
								handleFocus();
								props.onFocus?.(e);
							}
						}}
						{...selectProps}
					/>
				);

			case type === TYPE_CUSTOM_FIELD.mailAddress:
				return (
					<MailingAddress
						mailingAddress={field}
						setFieldsValue={setFieldsValue}
						isNewLayout={true}
						setUploadInfo={setFormValues}
						uploadInfo={formValues || {}}
						fieldProps={fieldProps}
					/>
				);

			case type === TYPE_CUSTOM_FIELD.paragraph:
				return (
					<StyledTextArea
						placeholder={name}
						rows={3}
						autoSize={{ minRows: 3, maxRows: 3 }}
						aria-label={`${field.name}${field.required ? "*" : ""}`}
					/>
				);

			default:
				return <Input placeholder={!isSpecialFields && type !== FIELD_NAME.email ? name : ""} />;
		}
	};

	const renderFormField = () => {
		if (type === TYPE_CUSTOM_FIELD.mailAddress) {
			return renderFormItemContent();
		}

		if (type === DEFAULT_FIELD_NAME.yourName) {
			const listField = [
				{
					name: FIELD_NAME.firstName,
					helperText: field?.placeholderFirstName,
					label: field.name,
				},
				{
					name: FIELD_NAME.lastName,
					helperText: field?.placeholderLastName,
					label: field.name,
					className: "hidden-label",
				},
			];
			return (
				<StyledRow gutter={[12, 12]}>
					{listField.map((item) => {
						return (
							<StyledCol key={item.name} span={12}>
								<StyledFormItem
									label={renderLabel(item.label, item.className)}
									name={item.name}
									help={item.helperText}
									rules={[
										{
											required,
											message: t("validation.required", {
												field: item.helperText,
											}),
										},
									]}>
									<Input />
								</StyledFormItem>
							</StyledCol>
						);
					})}
				</StyledRow>
			);
		}

		return <StyledFormItem {...fieldProps}>{renderFormItemContent()}</StyledFormItem>;
	};

	return (
		<StyledCol span={span} ref={wrapperRef}>
			{renderFormField()}
		</StyledCol>
	);
};

export default WidgetField;
